import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, BadgeCheck, Hourglass, MapPinOff, Phone, RefreshCw, UserX } from 'lucide-react';
import {
  Button, Card, Col, EntityAvatar, Field, Input, Modal, Row, Select, StatCard, Table, Tag, Textarea, Tooltip, message,
} from '../../ui';
import { request } from '../../api/client';
import { useConfirmedMutation } from '../../lib/confirm';
import { formatIstDate } from '../../lib/time';
import { FarmerDetailModal } from './FarmerDetailModal';

/**
 * FARMERS TO VERIFY — farmers who signed themselves up in the app, waiting for their sales person.
 *
 * The default view is exactly the reader's badge: their own farmers, a Sales Head's team farmers once
 * they are 3+ working days overdue, Admin's unowned and week-old ones. "Everyone in my area" widens to
 * the reader's whole scope so a Head can see what is coming. Verifying (or handing back) is the act
 * that clears a row; reassigning an unowned farmer is done in the farmer's own record.
 */

const STAGE_TONE: Record<string, 'blue' | 'gold' | 'red' | 'neutral'> = {
  NEW: 'blue', OVERDUE: 'gold', ESCALATED_SH: 'red', ESCALATED_ADMIN: 'red', UNOWNED: 'neutral',
};

type Row = {
  id: string; fullName: string; mobile: string; village?: string; pincode?: string; stateName?: string;
  districtText?: string | null; territoryId?: string | null; territoryName?: string | null; territoryLevel?: string | null;
  routingNote?: string | null; platform?: string | null; createdAt: string; ownerId?: string | null; ownerName?: string | null;
  workingDaysOld: number; stage: string; stageLabel: string; dueInWorkingDays: number; mine: boolean; actionable: boolean;
};

export function FarmerVerifyTab() {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const focusId = sp.get('farmer');
  const all = sp.get('scope') === 'all';
  const stage = sp.get('stage') ?? '';
  const setParam = (k: string, v?: string) => setSp((prev) => {
    const next = new URLSearchParams(prev);
    if (v) next.set(k, v); else next.delete(k);
    return next;
  }, { replace: true });

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ['farmer-verification', all],
    queryFn: () => request<{ items: Row[]; counts: { actionable: number; all: number; unowned: number; overdue: number } }>({
      url: '/farmer-verification', params: all ? { all: 1 } : {},
    }),
  });
  // Tiles come from the whole scope so they do not change when a tile is clicked.
  const { data: wide } = useQuery({
    queryKey: ['farmer-verification', true],
    queryFn: () => request<{ items: Row[]; counts: any }>({ url: '/farmer-verification', params: { all: 1 } }),
  });

  const rows = useMemo(() => {
    const items = data?.items ?? [];
    if (stage === 'OVERDUE') return items.filter((r) => r.stage !== 'NEW' && r.stage !== 'UNOWNED');
    if (stage) return items.filter((r) => r.stage === stage);
    return items;
  }, [data, stage]);

  // A deep link to one farmer that is not in "needs me" widens the view so the row is actually there.
  useEffect(() => {
    if (!focusId || all || !data) return;
    if (!data.items.some((r) => r.id === focusId)) setParam('scope', 'all');
  }, [focusId, all, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const [verifying, setVerifying] = useState<Row | null>(null);
  const [handingBack, setHandingBack] = useState<Row | null>(null);
  const [viewing, setViewing] = useState<Row | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['farmer-verification'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
    qc.invalidateQueries({ queryKey: ['farmers'] });
  };

  const counts = wide?.counts ?? { actionable: 0, all: 0, unowned: 0, overdue: 0 };
  const mineCount = data && !all ? data.counts.actionable : (wide?.counts?.actionable ?? 0);

  const columns: any[] = [
    {
      title: 'Farmer', key: 'farmer', width: 230,
      render: (_: any, r: Row) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <EntityAvatar name={r.fullName} size={30} />
          <div className="min-w-0">
            <button type="button" className="block truncate text-left type-body-strong text-content hover:text-brand-700" onClick={() => setViewing(r)}>
              {r.fullName}
            </button>
            <a href={`tel:${r.mobile}`} className="inline-flex items-center gap-1 type-caption text-muted hover:text-brand-700">
              <Phone size={12} aria-hidden /> {r.mobile}
            </a>
          </div>
        </div>
      ),
    },
    {
      title: 'Where', key: 'where', width: 320,
      render: (_: any, r: Row) => (
        <div className="min-w-0">
          <div className="truncate type-body text-content">
            {[r.village, r.territoryName ?? r.districtText, r.stateName].filter(Boolean).join(', ')}
            {r.pincode ? <span className="text-muted"> · {r.pincode}</span> : null}
          </div>
          {r.routingNote && <div className="truncate type-caption text-muted" title={r.routingNote}>{r.routingNote}</div>}
        </div>
      ),
    },
    {
      title: 'Signed up', key: 'at', width: 140,
      render: (_: any, r: Row) => (
        <span className="block leading-tight">
          <span className="block whitespace-nowrap">{formatIstDate(r.createdAt)}</span>
          <span className="block whitespace-nowrap type-caption text-muted">
            {r.workingDaysOld === 0 ? 'today' : `${r.workingDaysOld} working ${r.workingDaysOld === 1 ? 'day' : 'days'} ago`}
          </span>
        </span>
      ),
    },
    {
      title: 'Status', key: 'stage', width: 160,
      render: (_: any, r: Row) => (
        <div className="flex flex-col items-start gap-0.5">
          <Tag tone={STAGE_TONE[r.stage] ?? 'neutral'}>{r.stageLabel}</Tag>
          {r.stage === 'NEW' && <span className="type-caption text-muted">due in {r.dueInWorkingDays} working {r.dueInWorkingDays === 1 ? 'day' : 'days'}</span>}
        </div>
      ),
    },
    {
      title: 'Sales person', key: 'owner', width: 170,
      render: (_: any, r: Row) => (r.ownerName
        ? <span className="inline-flex min-w-0 items-center gap-2"><EntityAvatar name={r.ownerName} size={22} /><span className="truncate">{r.ownerName}{r.mine ? ' (you)' : ''}</span></span>
        : <span className="type-body text-danger-600">Nobody yet</span>),
    },
    {
      title: '', key: 'act', width: 200, align: 'right',
      render: (_: any, r: Row) => (
        <div className="flex justify-end gap-1.5">
          {r.ownerId ? (
            <Button size="sm" variant="primary" icon={<BadgeCheck size={15} />} onClick={() => setVerifying(r)}>Verify</Button>
          ) : (
            <Button size="sm" onClick={() => setViewing(r)}>Assign</Button>
          )}
          {r.mine && (
            <Tooltip label="This farmer is outside your area">
              <Button size="sm" variant="ghost" icon={<MapPinOff size={15} />} aria-label="Not my area" onClick={() => setHandingBack(r)} />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const tile = (label: string, value: number, tone: any, icon: React.ReactNode, active: boolean, go: () => void, isBadge = false) => (
    <StatCard label={label} value={value} tone={tone} icon={icon} active={active} onClick={value ? go : undefined}
      delta={isBadge && value ? { label: 'menu badge', direction: 'flat' } : undefined} />
  );

  return (
    <div>
      <p className="mb-3 type-body text-muted">
        Farmers who signed up in the app. Call or visit them within 2 working days to confirm who they are and where they farm.
      </p>
      <Row align="stretch" gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>{tile('Needs you', mineCount, mineCount ? 'brand' : 'neutral', <BadgeCheck size={18} />, !all && !stage,
          () => { setParam('scope'); setParam('stage'); }, true)}</Col>
        <Col xs={12} md={6}>{tile('Waiting in your area', counts.all, 'sky', <Hourglass size={18} />, all && !stage,
          () => { setParam('scope', 'all'); setParam('stage'); })}</Col>
        <Col xs={12} md={6}>{tile('Overdue', counts.overdue, counts.overdue ? 'warning' : 'neutral', <AlertTriangle size={18} />, all && stage === 'OVERDUE',
          () => { setParam('scope', 'all'); setParam('stage', 'OVERDUE'); })}</Col>
        <Col xs={12} md={6}>{tile('No sales person', counts.unowned, counts.unowned ? 'danger' : 'neutral', <UserX size={18} />, all && stage === 'UNOWNED',
          () => { setParam('scope', 'all'); setParam('stage', 'UNOWNED'); })}</Col>
      </Row>
      <Card density="compact">
        <div className="mb-3 flex items-center justify-between gap-2">
          <span className="type-caption text-muted">
            {all ? 'Everyone waiting in your area' : 'Farmers whose next step is yours'}{stage ? ` · ${stage === 'OVERDUE' ? 'overdue' : 'no sales person'}` : ''}
          </span>
          <Tooltip label="Refresh"><Button icon={<RefreshCw size={16} />} loading={isFetching} onClick={() => refetch()} aria-label="Refresh" /></Tooltip>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={isFetching && !data}
          pagination={false}
          rowClassName={(r: Row) => (r.id === focusId ? 'bg-brand-50 dark:bg-brand-500/10' : '')}
          locale={{ emptyText: isError ? 'Could not load the list — press Refresh.' : all ? 'Nobody is waiting to be verified.' : 'Nothing waiting on you.' }}
        />
      </Card>

      <VerifyModal row={verifying} onClose={() => setVerifying(null)} onDone={refresh} />
      <HandBackModal row={handingBack} onClose={() => setHandingBack(null)} onDone={refresh} />
      <FarmerDetailModal farmer={viewing} open={!!viewing} onClose={() => { setViewing(null); refresh(); }} />
    </div>
  );
}

type TreeNode = { id: string | number; name: string; level: string; children?: TreeNode[] };

/** Beats under a territory — the finer place an SP can pin the farmer to while verifying. */
function beatsUnder(tree: TreeNode[], territoryId?: string | null): { id: string; name: string }[] {
  if (!territoryId) return [];
  const find = (nodes: TreeNode[]): TreeNode | null => {
    for (const n of nodes) {
      if (String(n.id) === String(territoryId)) return n;
      const hit = find(n.children ?? []);
      if (hit) return hit;
    }
    return null;
  };
  const root = find(tree);
  const out: { id: string; name: string }[] = [];
  const walk = (n: TreeNode) => { for (const c of n.children ?? []) { if (c.level === 'beat') out.push({ id: String(c.id), name: c.name }); walk(c); } };
  if (root) walk(root);
  return out;
}

function VerifyModal({ row, onClose, onDone }: { row: Row | null; onClose: () => void; onDone: () => void }) {
  const [village, setVillage] = useState('');
  const [beat, setBeat] = useState<string | null>(null);
  const [note, setNote] = useState('');
  useEffect(() => { if (row) { setVillage(row.village ?? ''); setBeat(null); setNote(''); } }, [row]);
  const { data: tree = [] } = useQuery<TreeNode[]>({
    queryKey: ['territories-tree-picker'],
    queryFn: () => request<TreeNode[]>({ url: '/territories/tree' }),
    enabled: !!row,
  });
  const beats = useMemo(() => beatsUnder(tree, row?.territoryId), [tree, row]);

  const m = useConfirmedMutation(
    { title: `Mark ${row?.fullName ?? 'this farmer'} as verified?`, content: 'Only if you have spoken to them and the details are right.' },
    {
      mutationFn: () => request({
        url: `/farmer-verification/${row!.id}/verify`, method: 'POST',
        data: { village: village.trim() || undefined, territoryId: beat || undefined, note: note.trim() || undefined },
      }),
      onSuccess: () => { message.success('Farmer verified'); onDone(); onClose(); },
      onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not verify'),
    },
  );

  return (
    <Modal open={!!row} onOpenChange={(o) => { if (!o) onClose(); }} title={`Verify ${row?.fullName ?? ''}`} width={480}
      description="Confirm this is a real farmer at this place. Correct the village if needed."
      footer={<div className="flex justify-end gap-2"><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Verify</Button></div>}>
      {row && (
        <div className="flex flex-col gap-4">
          <div className="rounded-lg bg-surface-2 p-3 type-body">
            <div className="type-body-strong">{row.fullName} · <a className="text-brand-700" href={`tel:${row.mobile}`}>{row.mobile}</a></div>
            <div className="text-muted">{[row.village, row.territoryName ?? row.districtText, row.stateName].filter(Boolean).join(', ')} {row.pincode ? `· ${row.pincode}` : ''}</div>
          </div>
          <Field label="Village or town" htmlFor="vf-village"><Input id="vf-village" value={village} maxLength={120} onChange={(e) => setVillage(e.target.value)} /></Field>
          {beats.length > 0 && (
            <Field label="Beat" hint="Optional — the beat this farmer belongs to.">
              <Select allowClear placeholder="Keep as it is" value={beat} onChange={(v) => setBeat(v ?? null)}
                options={beats.map((b) => ({ value: b.id, label: b.name }))} />
            </Field>
          )}
          <Field label="Note" hint="Optional — e.g. how you confirmed them."><Textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        </div>
      )}
    </Modal>
  );
}

function HandBackModal({ row, onClose, onDone }: { row: Row | null; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState('');
  useEffect(() => { if (row) setNote(''); }, [row]);
  const m = useConfirmedMutation(
    { title: `Hand ${row?.fullName ?? 'this farmer'} back?`, content: 'They leave your list. Your Sales Head and Admin are told so the right person can take them.' },
    {
      mutationFn: () => request({ url: `/farmer-verification/${row!.id}/not-my-area`, method: 'POST', data: { note: note.trim() } }),
      onSuccess: () => { message.success('Handed back'); onDone(); onClose(); },
      onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not hand back'),
    },
  );
  const tooShort = note.trim().length < 3;
  return (
    <Modal open={!!row} onOpenChange={(o) => { if (!o) onClose(); }} title="Not in my area" width={460}
      footer={<div className="flex justify-end gap-2"><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={tooShort} loading={m.isPending} onClick={() => m.mutate()}>Hand back</Button></div>}>
      <Field label="Where does this farmer actually farm?" required hint="So Admin can give them to the right sales person.">
        <Textarea rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Niphad taluka — covered by Asmita" />
      </Field>
    </Modal>
  );
}
